import PlusCircleIcon from "@assets/svg/PlusCircleIcon";
import { getJobPostingFee } from "@endpoints/payment.service";
import {
  createPaymentIntent,
  getPaymentMethods,
} from "@endpoints/stripe.service";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import PaymentCard from "./PaymentCard";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import dayjs from "dayjs";
import { getClassificationSummary } from "@utils/classification";

const Finish = ({ fields,onPaymentSuccess,currentJobId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [jobPostingFee, setJobPostingFee] = useState(0);
  const [cardList, setCardList] = useState([]);
  const [isCardLoading, setIsCardLoading] = useState(true);
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  // Fee breakdown (all values in pounds).
  // VAT (20%, aka platform commission) is charged on the job posting fee.
  // Stripe transaction fee (2.9% + £0.30) is charged on the subtotal.
  const vat = jobPostingFee * 0.2;
  const subtotal = jobPostingFee + vat;
  const transactionFee = jobPostingFee > 0 ? subtotal * 0.029 + 0.3 : 0;
  const totalPayable = subtotal + transactionFee;

  async function fetchJobPostingFee() {
    try {
      const { success, data } = await getJobPostingFee();
      if (success) {
        setJobPostingFee(data?.amount);
      }
    } catch (error) {
      console.error("Error: Posting job posing fee", error);
    }
  }

  const fetchCards = async () => {
    await getPaymentMethods("card")
      .then((response) => {
        if (response.success) setCardList(response.data);
      })
      .catch((error) => enqueueSnackbar(error, { variant: "error" }))
      .finally(() => setIsCardLoading(false));
  };

  const handlePayment = () => {
    const paymentMethodId = selectedPaymentDetails?.id;
    if (!paymentMethodId) {
      enqueueSnackbar("Please select the payment method.", {
        variant: "error",
      });
      return;
    }
    setIsPaymentLoading(true);
    // Only the job posting fee is sent; VAT and transaction fee are computed on the backend.
    const amount = jobPostingFee;
    const currency = "gbp";
    if (!jobPostingFee || jobPostingFee == 0) {
      enqueueSnackbar("Amount is required", { variant: "error" });
      return;
    }
    console.log("total", amount, currency, paymentMethodId);

    if (!paymentMethodId) {
      enqueueSnackbar("Please select payment method", { variant: "error" });
      return;
    }
    createPaymentIntent(amount, currency, paymentMethodId, currentJobId)
      .then(async (response) => {
        if (response.success) {
          if (onPaymentSuccess) {
            await onPaymentSuccess();
            enqueueSnackbar("Payment made successfully", {
              variant: "success",
            });
          }
        }
      })
      .catch((error) => enqueueSnackbar(error, { variant: "error" }))
      .finally(() => setIsPaymentLoading(false));
  };

  useEffect(() => {
    fetchJobPostingFee();
    fetchCards();
  }, []);

  // console.log('Finish fields',fields)

  return (
    <div className="flex flex-1 rounded-md min-h-0 border">
      {/* Left section */}
      <div className="w-1/2 flex flex-col flex-1 min-h-0 border-r px-6 py-4 overflow-y-scroll no-scrollbar">
        <span className="font-bold text-sm pb-3">Job summary</span>
        <div className="flex flex-col py-4 border-y gap-2">
          <div className="flex items-center font-semibold text-xs gap-2">
            Job ID:
            <span className="font-medium text-xs text-light-black">{fields?.jobId||'JO-2341'}</span>
          </div>
          <div className="flex items-center font-semibold text-xs gap-2">
            Job Title:
            <span className="font-medium text-xs text-light-black">
              {fields?.jobTitle||'N/A'}
            </span>
          </div>
          <div className="flex items-center font-semibold text-xs gap-2">
            Classification:
            <span className="font-medium text-xs text-light-black">
              {getClassificationSummary(fields?.classification)}
            </span>
          </div>
          <div className="flex items-center font-semibold text-xs gap-2">
            Scheduled publish time:
            <span className="font-medium text-xs text-light-black">{dayjs(fields?.publishDate)?.format('Do MMM YYYY')}</span>
          </div>
        </div>

        <div className="flex flex-col py-4 border-b gap-3">
          <div className="flex items-center font-semibold text-xs gap-8">
            Job Posting Fee
            <div className="flex items-center gap-3">
              £
              <div className="flex items-center pl-3 rounded-md h-9 w-28 border">
                {jobPostingFee}
              </div>
            </div>
          </div>
          <span className="font-medium text-xs text-light-black">
            If the job placement is not successful through COMTRACKED the the
            Job posting fee will be refunded*
          </span>
        </div>

        {/* Payment method */}
        <div className="flex flex-col mt-6">
          <span className="font-bold text-sm pb-3">Payment method</span>
          <div className="flex flex-col font-medium text-xs gap-3.5 border-y pt-3 pb-7">
            Paying with
            <div className="flex flex-col gap-3">
              {cardList?.map((card) => {
                const isSelected = selectedPaymentDetails?.id === card?.id;
                return (
                  <PaymentCard
                    key={card?._id}
                    data={card}
                    isSelected={isSelected}
                    onClick={() => {
                      if (selectedPaymentDetails?.id === card?.id) {
                        setSelectedPaymentDetails(null);
                      } else {
                        setSelectedPaymentDetails(card);
                      }
                    }}
                  />
                );
              })}
            </div>
          </div>
          <button
            type="button"
            className="flex items-center justify-center gap-2 border rounded-md mt-6 py-3 font-medium text-xs text-light-black hover:bg-gray-50"
          >
            <PlusCircleIcon fill="gray" />
            Add Payment Method
          </button>
        </div>
      </div>
      {/* Right Section */}
      <div className="w-1/2 flex flex-col flex-1 min-h-0 px-6 py-4 overflow-y-scroll no-scrollbar">
        <span className="font-bold text-sm pb-3">Fee's summary</span>

        <div className="flex flex-col gap-2 py-8 border-y">
          <div className="flex items-center font-medium text-light-black text-xs">
            <span className="w-36">Job Posting Fee</span>
            <div className="flex items-center gap-3">
              £
              <div className="flex items-center pl-3 rounded-md h-9 w-28 border">
                {Number(jobPostingFee).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="flex items-center font-medium text-light-black text-xs">
            <span className="w-36">VAT(20%)</span>
            <div className="flex items-center gap-3">
              £
              <div className="flex items-center pl-3 rounded-md h-9 w-28 border">
                {vat.toFixed(2)}
              </div>
            </div>
          </div>
          <div className="flex items-center font-medium text-light-black text-xs">
            <span className="w-36">Transaction Fee</span>
            <div className="flex items-center gap-3">
              £
              <div className="flex items-center pl-3 rounded-md h-9 w-28 border">
                {transactionFee.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
        {/* Total */}
        <div className="flex flex-col gap-2 py-5 border-b">
          <div className="flex items-center font-medium text-light-black text-xs">
            <span className="w-36">Total</span>
            <div className="flex items-center gap-3">
              £
              <div className="flex items-center pl-3 rounded-md h-9 w-28 border">
                {totalPayable.toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={handlePayment}
          type="button"
          className="flex items-center justify-center gap-3 bg-primary rounded-md mt-6 text-white font-semibold py-3 hover:bg-black text-sm"
        >
          Pay Now {isPaymentLoading ? <LoadingAnimation /> : null}
        </button>

        <div className="bg-black rounded font-bold text-sm text-white w-max p-1.5 mt-8">
          Powered by Stripe
        </div>
      </div>
    </div>
  );
};

export default Finish;
